import axios from 'axios'
import type { Paginated } from '../types/pagination'
import type {
  Attachment,
  CreateProjectData,
  CreateProjectInviteData,
  Project,
  ProjectInvitation,
  ProjectInvite,
  ProjectStatus,
  UpdateProjectData,
} from '../types/project'
import type { User } from '../types/user'
import { api } from './fetch'

/**
 * Extract IDs from an array that may contain User objects or numbers.
 * This normalizes the data before sending to the API.
 */
const extractUserIds = (
  users: (User | number)[] | undefined,
): number[] | undefined => {
  if (!users || users.length === 0) return undefined
  return users.map((user) => (typeof user === 'object' ? user.id : user))
}

/**
 * Extract contact data (id, email, phone) from User objects.
 * This sends the contact info along with the ID so the backend can create
 * snapshots with the correct (potentially removed) email/phone.
 */
const extractContactData = (
  users: (User | number)[] | undefined,
): { id: number; email: string | null; phone: string | null }[] | undefined => {
  if (!users || users.length === 0) return undefined
  return users.map((user) => {
    if (typeof user === 'object') {
      return {
        id: user.id,
        email: user.email || null,
        phone: user.phone_number || null,
      }
    }
    // If just an ID, we don't have override data
    return { id: user, email: null, phone: null }
  })
}

// In-flight request cache to deduplicate concurrent requests
const inFlightRequests = new Map<string, Promise<unknown>>()

interface ProjectFilters {
  dateFrom?: Date
  dateTo?: Date
  search?: string
  status?: ProjectStatus
}

export const projectService = {
  async getProjects(
    filters?: ProjectFilters & { page?: number; page_size?: number },
  ): Promise<Paginated<Project>> {
    const params = new URLSearchParams()

    if (filters?.dateFrom) {
      params.append('created_after', filters.dateFrom.toISOString())
    }
    if (filters?.dateTo) {
      params.append('created_before', filters.dateTo.toISOString())
    }
    if (filters?.search) {
      params.append('search', filters.search)
    }
    if (filters?.status) {
      params.append('status', String(filters.status))
    }
    if (filters?.page) {
      params.append('page', String(filters.page))
    }
    if (filters?.page_size) {
      params.append('page_size', String(filters.page_size))
    }

    const queryString = params.toString()
    const url = queryString ? `/project/?${queryString}` : '/project/'
    const cacheKey = `getProjects:${url}`

    // Return existing in-flight request if one exists
    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey) as Promise<Paginated<Project>>
    }

    // Create new request and cache it
    const requestPromise = (async () => {
      try {
        const response = await api.get<Paginated<Project> | Project[]>(url)
        const data = response.data as unknown
        if (Array.isArray(data)) {
          let items = data as Project[]
          if (filters?.status) {
            // Support comma-separated status values
            const statuses = String(filters.status)
              .split(',')
              .map((s) => s.trim())
            items = items.filter((p) => statuses.includes(p.status))
          }
          // order by -created_date
          items = [...items].sort(
            (a, b) =>
              new Date(b.created_date).getTime() -
              new Date(a.created_date).getTime(),
          )
          const total = items.length
          const currentPage = Math.max(1, filters?.page ?? 1)
          const size = Math.max(1, Math.min(filters?.page_size ?? total, total))
          const start = (currentPage - 1) * size
          const end = start + size
          const paged = items.slice(start, end)
          const pages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1
          return { items: paged, count: total, page: currentPage, pages }
        }
        return data as Paginated<Project>
      } catch (error) {
        console.error('Failed to fetch projects:', error)
        throw error
      } finally {
        inFlightRequests.delete(cacheKey)
      }
    })()

    inFlightRequests.set(cacheKey, requestPromise)
    return requestPromise
  },

  async saveProject(projectData: CreateProjectData): Promise<Project> {
    try {
      // Transform User objects to IDs and contact data before sending to API
      const apiData = {
        ...projectData,
        // Legacy ID fields (for backward compatibility)
        contractors: extractUserIds(
          projectData.contractors as (User | number)[] | undefined,
        ),
        site_supervisors:
          extractUserIds(
            projectData.site_supervisors as (User | number)[] | undefined,
          ) || [],
        sub_haulers: extractUserIds(
          projectData.sub_haulers as (User | number)[] | undefined,
        ),
        third_party_sub_haulers: extractUserIds(
          projectData.third_party_sub_haulers as (User | number)[] | undefined,
        ),
        third_party_contractors: extractUserIds(
          projectData.third_party_contractors as (User | number)[] | undefined,
        ),
        third_party_brokers: extractUserIds(
          projectData.third_party_brokers as (User | number)[] | undefined,
        ),
        brokers: extractUserIds(
          projectData.brokers as (User | number)[] | undefined,
        ),
        // Contact data with email/phone for snapshots
        contractor_data: extractContactData(
          projectData.contractors as (User | number)[] | undefined,
        ),
        broker_data: extractContactData(
          projectData.brokers as (User | number)[] | undefined,
        ),
        sub_hauler_data: extractContactData(
          projectData.sub_haulers as (User | number)[] | undefined,
        ),
        third_party_sub_hauler_data: extractContactData(
          projectData.third_party_sub_haulers as (User | number)[] | undefined,
        ),
        third_party_contractor_data: extractContactData(
          projectData.third_party_contractors as (User | number)[] | undefined,
        ),
        project_managers: extractUserIds(
          projectData.project_managers as (User | number)[] | undefined,
        ),
        administrators: extractUserIds(
          projectData.administrators as (User | number)[] | undefined,
        ),
        project_manager_data: extractContactData(
          projectData.project_managers as (User | number)[] | undefined,
        ),
        administrator_data: extractContactData(
          projectData.administrators as (User | number)[] | undefined,
        ),
      }

      console.log('Project data being sent:', JSON.stringify(apiData, null, 2))
      const response = await api.post('/project/', apiData)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Server validation errors:', error.response?.data)
        console.error('Error response full data:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  async createProjectInvite(
    projectId: number,
    inviteData: CreateProjectInviteData,
  ): Promise<ProjectInvite> {
    try {
      const response = await api.post(
        `/project/${projectId}/invite`,
        inviteData,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to create project invite:', error.response?.data)
        console.error('Error response full data:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  async getProjectInvites(projectId: number): Promise<ProjectInvite[]> {
    try {
      const response = await api.get(`/project/${projectId}/invites`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch project invites:', error)
      throw error
    }
  },

  async deleteProjectInvite(
    projectId: number,
    inviteId: number,
  ): Promise<void> {
    try {
      await api.delete(`/project/${projectId}/invite/${inviteId}`)
    } catch (error) {
      console.error('Failed to delete project invite:', error)
      throw error
    }
  },

  async updateProject(
    projectId: number,
    updateData: UpdateProjectData,
  ): Promise<Project> {
    try {
      // Transform User objects to IDs and contact data before sending to API
      const apiData = {
        ...updateData,
        // Legacy ID fields (for backward compatibility)
        contractors: extractUserIds(
          updateData.contractors as (User | number)[] | undefined,
        ),
        site_supervisors: updateData.site_supervisors
          ? extractUserIds(updateData.site_supervisors as (User | number)[])
          : undefined,
        sub_haulers: extractUserIds(
          updateData.sub_haulers as (User | number)[] | undefined,
        ),
        third_party_sub_haulers: extractUserIds(
          updateData.third_party_sub_haulers as (User | number)[] | undefined,
        ),
        third_party_contractors: extractUserIds(
          updateData.third_party_contractors as (User | number)[] | undefined,
        ),
        third_party_brokers: extractUserIds(
          updateData.third_party_brokers as (User | number)[] | undefined,
        ),
        brokers: extractUserIds(
          updateData.brokers as (User | number)[] | undefined,
        ),
        // Contact data with email/phone for snapshots
        contractor_data: extractContactData(
          updateData.contractors as (User | number)[] | undefined,
        ),
        broker_data: extractContactData(
          updateData.brokers as (User | number)[] | undefined,
        ),
        sub_hauler_data: extractContactData(
          updateData.sub_haulers as (User | number)[] | undefined,
        ),
        third_party_sub_hauler_data: extractContactData(
          updateData.third_party_sub_haulers as (User | number)[] | undefined,
        ),
        third_party_contractor_data: extractContactData(
          updateData.third_party_contractors as (User | number)[] | undefined,
        ),
        project_managers: extractUserIds(
          updateData.project_managers as (User | number)[] | undefined,
        ),
        administrators: extractUserIds(
          updateData.administrators as (User | number)[] | undefined,
        ),
        project_manager_data: extractContactData(
          updateData.project_managers as (User | number)[] | undefined,
        ),
        administrator_data: extractContactData(
          updateData.administrators as (User | number)[] | undefined,
        ),
      }

      const response = await api.patch(`/project/${projectId}`, apiData)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to update project:', error.response?.data)
        console.error('Error response full data:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  updateProjectStatus: async (
    projectId: string,
    status: ProjectStatus,
    declineReason?: string,
  ) => {
    try {
      const payload: { status: ProjectStatus; decline_reason?: string } = {
        status,
      }
      if (declineReason) {
        payload.decline_reason = declineReason
      }

      const response = await api.patch(`/project/${projectId}/status`, payload)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to update project status:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error updating project status:', error)
      }
      throw error
    }
  },

  getProject: async (projectId: string | number): Promise<Project> => {
    try {
      const response = await api.get(`/project/${projectId}`)
      return response.data
    } catch (error) {
      console.error('Failed to get project:', error)
      throw error
    }
  },

  getLabel: async (projectId: string | number): Promise<string> => {
    try {
      const project = await projectService.getProject(projectId)
      return project.label || '-'
    } catch (error) {
      console.error('Failed to get project label:', error)
      return '-'
    }
  },

  getProjectContractor: async (
    projectId: string | number,
  ): Promise<{ name: string; id: number } | null> => {
    try {
      const project = await projectService.getProject(projectId)
      if (project.created_by) {
        // project.created_by is now a full User object, no need for additional API call
        return {
          name:
            `${project.created_by.first_name || ''} ${project.created_by.last_name || ''}`.trim() ||
            '-',
          id: project.created_by.id,
        }
      }
      return null
    } catch (error) {
      console.error('Failed to get project contractor:', error)
      return null
    }
  },

  acceptProjectInvite: async (
    projectId: string | number,
    inviteId: string,
  ): Promise<{ success: boolean }> => {
    try {
      const response = await api.patch(
        `/project/${projectId}/invite/${inviteId}/accept`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to accept project invite:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error accepting project invite:', error)
      }
      throw error
    }
  },

  getUserPendingInvitations: async (
    projectId: string | number,
  ): Promise<ProjectInvitation[]> => {
    try {
      const response = await api.get(`/project/${projectId}/user-invitations`)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to get user pending invitations:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      } else {
        console.error('Error getting user pending invitations:', error)
      }
      throw error
    }
  },

  downloadProjectPdf: async (projectId: string | number): Promise<Blob> => {
    try {
      const response = await api.get(`/project/${projectId}/download/`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      console.error('Failed to download project PDF:', error)
      throw error
    }
  },

  sendDraft: async (projectId: number): Promise<Project> => {
    try {
      const response = await api.post(`/project/${projectId}/send`)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to send draft project:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  /**
   * Generate a preview project label and creation date for the confirmation step.
   * This label will be shown in the preview but the actual label
   * is generated by the backend when the project is created.
   */
  generateLabel: async (): Promise<{
    label: string
    creation_date_formatted: string
  }> => {
    try {
      const response = await api.get<{
        label: string
        creation_date_formatted: string
      }>('/project/generate-label/')
      return response.data
    } catch (error) {
      console.error('Failed to generate project label:', error)
      throw error
    }
  },

  /**
   * Upload attachments to a project.
   * Uses FormData to upload multiple files at once.
   */
  uploadAttachments: async (
    projectId: number,
    files: File[],
  ): Promise<Attachment[]> => {
    try {
      const formData = new FormData()
      for (const file of files) {
        formData.append('files', file)
      }

      const response = await api.post(
        `/project/${projectId}/attachments/upload/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data.attachments
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to upload attachments:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  /**
   * Delete an attachment from a project.
   */
  deleteAttachment: async (
    projectId: number,
    attachmentId: number,
  ): Promise<void> => {
    try {
      await api.delete(`/project/${projectId}/attachments/${attachmentId}/`)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to delete attachment:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  /**
   * Delete a project (typically used for drafts).
   */
  deleteProject: async (
    projectId: number,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete(`/project/${projectId}`)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to delete project:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  /**
   * Get ACCEPTED projects where the current user is assigned as a sub-hauler.
   * Used by sub-haulers to select a parent project when creating downstream project terms.
   * Supports pagination and search.
   */
  getAcceptedProjectsAsSubHauler: async (params?: {
    page?: number
    page_size?: number
    search?: string
  }): Promise<Paginated<Project>> => {
    try {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', String(params.page))
      if (params?.page_size)
        queryParams.append('page_size', String(params.page_size))
      if (params?.search) queryParams.append('search', params.search)

      const queryString = queryParams.toString()
      const url = queryString
        ? `/project/accepted-as-sub-hauler/?${queryString}`
        : '/project/accepted-as-sub-hauler/'

      const response = await api.get<Paginated<Project> | Project[]>(url)

      // Handle both paginated and legacy array responses
      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          count: response.data.length,
          page: 1,
          pages: 1,
        }
      }
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to get accepted projects as sub-hauler:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },

  /**
   * Get ACCEPTED projects where the current user is assigned as a broker.
   * Used by brokers to select a parent project when creating downstream project terms.
   * Supports pagination and search.
   */
  getAcceptedProjectsAsBroker: async (params?: {
    page?: number
    page_size?: number
    search?: string
  }): Promise<Paginated<Project>> => {
    try {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', String(params.page))
      if (params?.page_size)
        queryParams.append('page_size', String(params.page_size))
      if (params?.search) queryParams.append('search', params.search)

      const queryString = queryParams.toString()
      const url = queryString
        ? `/project/accepted-as-broker/?${queryString}`
        : '/project/accepted-as-broker/'

      const response = await api.get<Paginated<Project> | Project[]>(url)

      // Handle both paginated and legacy array responses
      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          count: response.data.length,
          page: 1,
          pages: 1,
        }
      }
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to get accepted projects as broker:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          details:
            error.response?.data?.detail || error.response?.data?.message,
        })
      }
      throw error
    }
  },
}

export type { ProjectFilters }
